<script setup>

import { inject, ref, computed, onMounted } from 'vue';
import { apiGetJeuxDonnees } from '@/services/ServiceModele.js';
import { decrireStatutJeuDonnees } from '@/scripts/global.js';

import Dropdown from 'primevue/dropdown';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';

const { selection, campagneParDefaut, nomsCampagnes, selectionMultiple, estJeuDesactive, estJeuSelectionneParDefaut } = defineProps({
    selection: [Object, Array],
    campagneParDefaut: String,
    nomsCampagnes: Array,
    selectionMultiple: Boolean,
    estJeuDesactive: {
        type: Function,
        default: () => { return false; }
    },
    estJeuSelectionneParDefaut: {
        type: Function,
        default: () => { return false; }
    }
});

const emit = defineEmits(['update:selection']);

const schema = inject('schema');
const appelerApiModele = inject('appelerApiModele');
const afficherErreur = inject('afficherErreur');

const jeuxDonnees = ref([]);
const campagneSelectionnee = ref(campagneParDefaut);

const proxySelection = computed({
    get: () => selection,
    set: (val) => emit('update:selection', val)
});

onMounted(() => {
    rafraichirJeuxDonnees();
});

function rafraichirJeuxDonnees() {
    appelerApiModele(apiGetJeuxDonnees, [campagneSelectionnee.value], function(reponseOK, contenu) {
        if (reponseOK) {
            jeuxDonnees.value = contenu.jeuxDonnees;
            emit('update:selection', getSelectionParDefaut(jeuxDonnees.value));
        } else {
            jeuxDonnees.value.length = 0;
            emit('update:selection', getSelectionVide());
            afficherErreur('Erreur lors de la récupération des jeux de données existants');
        }
    });
}

function getClasseLigne(jeuDonnees) {
    if (estJeuDesactive(jeuDonnees)) {
        return 'jeu-desactive';
    }
}

function getSelectionParDefaut(jeuxDonnees) {
    if (selectionMultiple) {
        return jeuxDonnees.filter(jeu => estJeuSelectionneParDefaut(jeu));
    } else {
        return jeuxDonnees.find(jeu => estJeuSelectionneParDefaut(jeu));
    }
}

function getSelectionVide() {
    return selectionMultiple ? [] : null;
}

</script>

<template>
    <div class="selecteur-jeu-donnees">
        <div class="zone-choix-campagne">
            <label class="intitule">Campagne : </label>
            <Dropdown 
                v-model="campagneSelectionnee" 
                :options="nomsCampagnes" 
                @change="rafraichirJeuxDonnees()" />
        </div>
        <DataTable 
            :value="jeuxDonnees" 
            v-model:selection="proxySelection"
            dataKey="id"
            :selectionMode="selectionMultiple ? 'multiple' : 'single'"
            :metaKeySelection="selectionMultiple"
            paginator 
            :rows="10" 
            tableStyle="min-width: 50rem"
            :rowClass="getClasseLigne">
            <template v-for="(parametre, indiceParametre) in schema.parametres">
                <Column :field="parametre.nom" :header="parametre.libelle" sortable>
                    <template #body="slotProps">
                        <label class="contenu-colonne">{{ slotProps.data.hypotheses[indiceParametre].valeurFormatee }}</label>
                    </template>                        
                </Column>
            </template>
            <Column field="dernierCalcul" header="Date de dernier recalcul" sortable>
                <template #body="slotProps">
                    <label class="contenu-colonne">{{ slotProps.data.dernierCalcul ? (new Date(slotProps.data.dernierCalcul)).toLocaleString() : '' }}</label>
                </template>                        
            </Column>
            <Column field="statut" header="Statut" sortable>
                <template #body="slotProps">
                    <label class="contenu-colonne">{{ decrireStatutJeuDonnees(slotProps.data.statut) }}</label>
                </template>                        
            </Column>
        </DataTable>
    </div>
</template>

<style scoped>

.selecteur-jeu-donnees {
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: center;
}

.zone-choix-campagne {
    display: flex;
    flex-direction: row;
    gap: 5px;
}

:deep(.jeu-desactive) {
    pointer-events: none;
    opacity: 0.5;
    background-color: #f5f5f5 !important;
}

</style>